import { useEffect, useState } from "react";
import localforage from "localforage";
import { Link, Outlet, useLocation } from "react-router-dom";
import { PanelLeft, Search } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { cn } from "lib/utils";
import maybeLogo from "../../assets/maybe-logo.png";
import { useUserInfo } from "../../context/user";
import { buildAuthedPath } from "../../lib/buildAuthedPath";
import { loadConfig } from "../../config";
import env from "../../config/env";
import AppNav from "./AppNav";
import ConfigureMenu from "./ConfigureMenu";
import ThemeToggle from "./ThemeToggle";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import ChatHistorySidebar from "../../pages/Chat/components/ChatHistorySidebar";

/**
 * The app-wide layout for the unified app shell (Slice A). Mounted as a
 * react-router layout route, so page content renders through <Outlet/>.
 *
 * Structure mirrors the Lovable AppHeader + AppSidebar model, reusing the
 * existing chat Layout dimensions (an ~85px top header, body filling the
 * remaining viewport height):
 *
 *  - Top header (full width, ~85px): brand/agency logo on the left
 *    (agency Customization.logo if present, else the default brand SvgIcon -
 *    same logic as Navbar), WorkspaceSwitcher on the right. The header paints
 *    the unified neutral background (bg-background) to match the left nav -
 *    the legacy agency Customization `topbar_bg` override is intentionally
 *    NOT applied here (unified-shell neutral). The theme toggle lives in the
 *    sidebar control row, not the header, matching the Lovable AppSidebar.
 *  - Body row (calc(100vh-85px)): a left sidebar stacking the primary-nav
 *    zone (AppNav) above the existing conversation-history sidebar
 *    (ChatHistorySidebar, rendered as-is - its re-skin is Slice B), and a
 *    scrolling content area (<Outlet/>) filling the rest. The history zone
 *    only mounts on chat routes (it fetches conversations on mount), so the
 *    non-chat pages (Settings/Agents/etc.) show just AppNav. This gating is a
 *    deliberate interim per the plan; always-visible history with a cached
 *    fetch is revisited in Slice B.
 *
 * Reads the agency data the same way Navbar does (useUserInfo positional
 * array). WorkspaceSwitcher reads its own context and takes no props; AppNav /
 * ConfigureMenu / ChatHistorySidebar take a `collapsed` prop driven by the
 * rail toggle at the top of the sidebar.
 */
const HEADER_HEIGHT = "64px";

export default function AppShell() {
  const userInfo = useUserInfo();
  const user = userInfo[0];
  const activeLocation = userInfo[4];
  const isValidEmail = userInfo[6];
  const isValidLocationId = userInfo[7];
  const agencyFromUser = userInfo[11];
  const sessionVerified = userInfo[24];

  // AppShell mounts as soon as cached localforage data satisfies the /app/*
  // guard, which happens before the backend has actually confirmed the
  // session - so `user?._id` alone isn't "logged in," just "something is
  // cached." sessionVerified only flips true once the backend has confirmed
  // the token (checkLocation on a fresh login, getUser on a resumed one).
  //
  // Re-runs the same /frontend config fetch bootstrap() does at app load
  // (src/index.js), since that first call usually fires pre-login (userId
  // falls back to the "user" placeholder) - this refreshes the shared `env`
  // object with the real user's config once one actually exists.
  useEffect(() => {
    if (!user?._id || sessionVerified !== true) return;
    // Read from localforage rather than user.token: the fresh-login path
    // (sign-in/index.jsx) only ever persists the token there, it never
    // merges it into context state the way a resumed session's
    // getUser/updateUser does - localforage is the one source that's
    // reliably populated on both paths.
    localforage.getItem("token").then((token) => {
      loadConfig(token).then((config) => Object.assign(env, config));
    });
  }, [user?._id, sessionVerified]);

  // The brand logo links to the chat home — the same encoded target as the
  // "New Task" nav item — mirroring the Lovable shell (logo === New Task).
  const homePath = buildAuthedPath("/app/chat", {
    email: user?.email,
    locationId: activeLocation?.id,
    isValidEmail,
    isValidLocationId,
  });

  // ChatHistorySidebar fetches conversations on mount, so only mount it on
  // chat routes - other pages (Settings/Agents/etc.) show just AppNav.
  const { pathname } = useLocation();
  const isChatRoute = pathname.includes("/chat");

  // Owns the sidebar collapse + search-open state so the search + collapse
  // controls can sit at the top of the rail (above AppNav), matching the
  // Lovable AppSidebar. Collapsing minifies the whole nav to an icon rail;
  // the search toggle expands an inline search field (search behaviour TBD).
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const toggleRail = () =>
    setRailCollapsed((prev) => {
      const next = !prev;
      if (next) setSearchOpen(false);
      return next;
    });

  const chipClass =
    "inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent transition-colors cursor-pointer text-muted-foreground hover:text-foreground hover:border-foreground/15 hover:bg-muted";

  // Match Navbar's resolution: prefer the agency from context when populated,
  // else the agency carried on the user. AppShell only needs the agency for
  // the header logo + background, so the user-carried agency is sufficient.
  const agencyData = agencyFromUser;
  const customization = agencyData?.Customization;

  return (
    <div className="flex h-screen flex-col bg-background">
      <header
        className="flex w-full shrink-0 items-center justify-between gap-4 border-b border-border/40 bg-background px-4 sm:px-6"
        style={{ height: HEADER_HEIGHT }}
      >
        <div className="flex items-center">
          <Link
            to={homePath}
            aria-label="Go to home"
            className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {/* Agency white-label logo if set, else the bundled Maybe* wordmark
                (the same local asset Lovable uses). Both sized to the Lovable header
                and inverted in dark mode. */}
            <img
              src={customization?.logo || maybeLogo}
              alt="Maybe"
              className="h-7 md:h-[2.1875rem] w-auto dark:invert"
            />
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <WorkspaceSwitcher />
        </div>
      </header>

      <div
        className="flex w-full bg-background"
        style={{ height: `calc(100vh - ${HEADER_HEIGHT})` }}
      >
        <aside
          className={cn(
            "flex shrink-0 flex-col overflow-hidden border-r border-border transition-[width] duration-200",
            railCollapsed ? "w-16" : "w-64"
          )}
        >
          <TooltipProvider>
            {/* Control row order matches the Lovable AppSidebar: Search,
                ThemeToggle, Collapse when expanded; collapsed stacks the
                chips in a column with the collapse control first. */}
            <div
              className={cn(
                "flex shrink-0 items-center px-3.5 pt-4 pb-1",
                railCollapsed
                  ? "flex-col justify-center gap-1"
                  : "justify-end gap-3"
              )}
            >
              {!railCollapsed && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="Search"
                      aria-expanded={searchOpen}
                      className={chipClass}
                      onClick={() => setSearchOpen((prev) => !prev)}
                    >
                      <Search className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Search</TooltipContent>
                </Tooltip>
              )}
              <ThemeToggle />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Toggle sidebar"
                    className={cn(chipClass, railCollapsed && "order-first")}
                    onClick={toggleRail}
                  >
                    <PanelLeft className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {railCollapsed ? "Open Sidebar" : "Close Sidebar"}
                </TooltipContent>
              </Tooltip>
            </div>

            {!railCollapsed && searchOpen && (
              <div className="px-3.5 pb-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    autoFocus
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search..."
                    aria-label="Search"
                    className="h-8 w-full rounded-md border border-input bg-background !pl-8 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:!border-foreground/70"
                  />
                </div>
              </div>
            )}
          </TooltipProvider>

          <div className="shrink-0 p-2">
            <AppNav collapsed={railCollapsed} />
          </div>
          {isChatRoute && (
            <div className="min-h-0 flex-1 overflow-hidden">
              <ChatHistorySidebar collapsed={railCollapsed} />
            </div>
          )}
          <div className="mt-auto shrink-0 border-t border-border p-2">
            <ConfigureMenu collapsed={railCollapsed} />
          </div>
        </aside>

        <main className="h-full w-full overflow-auto bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
